import type {
  TextFillData,
  TextFontData,
  TextLayoutData,
  TextMultilineData,
  TextShadowData,
  TextStrokeData,
  TextStyleData,
  TextStyleJsonData
} from '@/types';
import { FillGradient, type FillInput, TextStyle, type TextStyleOptions } from 'pixi.js';
// 假设你的接口定义在 ./types 中

/**
 * 核心工具：从 source 中提取指定的 keys，并过滤掉值为 undefined 的属性
 */
function pickDefined<T extends object, K extends keyof T>(
  source: T,
  keys: K[]
): Partial<Pick<T, K>> {
  const result: any = {};
  for (const key of keys) {
    if (source[key] !== undefined) {
      result[key] = source[key];
    }
  }
  return result;
}

/**
 * json数据转换成素材数据结构
 * @param jsonData
 * @returns
 */
export function convertJsonToData(jsonData: TextStyleJsonData | null): TextStyleData {
  if (!jsonData) return {};

  const result: TextStyleData = {};

  // 1. 字体 (映射字段名一致的情况)
  const font = pickDefined(jsonData, [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'fontVariant'
  ]);
  if (Object.keys(font).length > 0) result.font = font as TextFontData;

  // 2. 布局
  const layout = pickDefined(jsonData, [
    'trim',
    'align',
    'textBaseline',
    'lineHeight',
    'letterSpacing',
    'leading',
    'padding'
  ]);
  if (Object.keys(layout).length > 0) result.layout = layout as TextLayoutData;

  // 3. 多行文本
  const multiline = pickDefined(jsonData, [
    'wordWrap',
    'wordWrapWidth',
    'breakWords',
    'whiteSpace'
  ]);
  if (Object.keys(multiline).length > 0) result.multiline = multiline as TextMultilineData;

  // 4. 阴影 (只有当 dropShadow 为 true 时才处理)
  if (jsonData.dropShadow) {
    result.shadow = {
      alpha: jsonData.dropShadowAlpha ?? 1,
      angle: jsonData.dropShadowAngle ?? 0,
      blur: jsonData.dropShadowBlur ?? 4,
      color: jsonData.dropShadowColor ?? '#000000',
      distance: jsonData.dropShadowDistance ?? 5
    };
  }

  // 5. 描边 (字段名转换)
  if (jsonData.stroke !== undefined || jsonData.strokeThickness !== undefined) {
    const stroke: TextStrokeData = {
      color: jsonData.stroke ?? '#000000',
      width: jsonData.strokeThickness ?? 0
    };
    if (jsonData.lineJoin) stroke.join = jsonData.lineJoin as any;
    if (jsonData.miterLimit) stroke.miterLimit = jsonData.miterLimit;
    result.stroke = stroke;
  }

  // 6. 填充 (特殊逻辑：区分渐变和普通填充)
  if (jsonData.fill !== undefined) {
    const fillData: TextFillData = { fill: jsonData.fill };

    if (Array.isArray(jsonData.fill)) {
      fillData.type = 'linear';
      fillData.stops = jsonData.fillGradientStops;
      // 0 代表垂直，1 代表水平, 默认不传是0
      const isVertical = jsonData.fillGradientType !== 1;
      fillData.start = { x: 0, y: 0 };
      fillData.end = isVertical ? { x: 0, y: 1 } : { x: 1, y: 0 };
    }

    result.fill = fillData;
  }

  return result;
}

/**
 * 根据TextStyleData创建TextStyle
 * @param data
 * @returns
 */
export function createTextStyle(data: TextStyleData) {
  //字段一致，直接合并
  const textStyleOptions: TextStyleOptions = {
    ...data.font,
    ...data.layout,
    ...data.multiline
  };

  // 根据数据生成FillInput
  const fill = createTextStyleFill(data.fill);
  if (fill) textStyleOptions.fill = fill;

  // 根据数据生成Stroke，现在只是浅表复制，后续考虑加入复杂的渐变
  const stroke = createTextStyleStroke(data.stroke);
  if (stroke) textStyleOptions.stroke = stroke;

  //浅表复制一下阴影属性
  const shadow = createTextStyleShadow(data.shadow);
  if (data.shadow) textStyleOptions.dropShadow = shadow;

  // 当存在描边且未显式设置 padding 时，自动添加内边距，
  // 防止描边在画布纹理边缘被裁切，导致缩放后笔画时隐时现
  if (data.stroke && data.stroke.width > 0 && textStyleOptions.padding === undefined) {
    textStyleOptions.padding = data.stroke.width + 4;
  }

  return new TextStyle(textStyleOptions);
}

/**
 * 根据数据创建FillInput
 * @param data
 * @returns
 */
export function createTextStyleFill(data?: TextFillData): FillInput | undefined {
  if (!data) return;

  // 1. 如果只是单色，直接返回颜色
  if (typeof data.fill === 'string') {
    return { color: data.fill, alpha: data.alpha ?? 1 };
  }

  // 2. 如果是渐变数组，构建 FillGradient
  if (data.fill.length === 1) {
    // 只有一个色值时退化成纯色，避免 offset 计算出现 NaN（0/0）
    return { color: data.fill[0], alpha: data.alpha ?? 1 };
  }

  let gradient: FillGradient;

  const colorStops = data.fill.map((color: any, index: number) => ({
    color: color,
    offset: Number.isFinite(data.stops?.[index])
      ? (data.stops?.[index] as number)
      : index / (data.fill.length - 1)
  }));

  if (data.type === 'radial') {
    //径向渐变
    /**
     * textureSpace local和global的区别:
     * local把左边归化为了0-1， (0，0)是左上角，(1，1)是右下角
     * global 采取的是正常的坐标值
     */

    gradient = new FillGradient({
      type: 'radial',
      center: data.start ?? { x: 0.5, y: 0.5 },
      innerRadius: data.startRadius ?? 0,
      outerCenter: data.end ?? { x: 0.5, y: 0.5 },
      outerRadius: data.endRadius ?? 1,
      colorStops,
      textureSpace: 'local'
    });
  } else {
    //线性渐变
    gradient = new FillGradient({
      type: 'linear',
      start: data.start ?? { x: 0, y: 0 },
      end: data.end ?? { x: 1, y: 0 },
      colorStops,
      textureSpace: 'local'
    });
  }

  return gradient;
}

/** 描边与阴影：如果字段完全一致，也可以简写 */
export function createTextStyleStroke(data?: TextStrokeData) {
  if (!data) return undefined;
  // 确保 alpha 有默认值，避免 undefined 传入 PixiJS 后
  // handleFillObject 中 undefined *= 1 → NaN 导致描边颜色渲染为黑色
  return { alpha: 1, ...data };
}

export function createTextStyleShadow(data?: TextShadowData) {
  if (!data) return undefined;

  const alpha = Number.isFinite(data.alpha) ? Math.min(1, Math.max(0, data.alpha as number)) : 1;
  const angle = Number.isFinite(data.angle) ? (data.angle as number) : 0;
  const blur = Number.isFinite(data.blur) ? (data.blur as number) : 0;
  const distance = Number.isFinite(data.distance) ? (data.distance as number) : 0;

  return {
    color: data.color ?? '#000000',
    alpha,
    angle,
    blur,
    distance
  };
}
